import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useListState } from '@sas/nova-commons/components/List/useListState';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Divider } from '@sas/nova-commons/components/Divider';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { AddRoute, DisclosureNext, GeoUIAreaAddress, Hidden, Location, Overflow, UserSession, Visible, } from '@sas/react-svg';
import { GroupHeaderItem, Item, List, } from '@sas/nova-commons/components/List';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { MenuButton } from '@sas/nova-commons/components/MenuButton';
import { useDesignTokens, renderInlineCssVars } from '@sas/nova-commons/theme';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../../../locale/I18nContext';
import { PanelListItem, getItemDescription } from '../components/PanelListItem';
import { ObjectSearchState, isSearchHint, isSearchResult, isGeographicFeature, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { Demographics } from '../GeographicAreaPanel/Demographics';
import { AutoFocusInside } from 'react-focus-lock';
import { TableRegions } from '@sas/nova-commons/components/Table';
import { usePreviousValue } from '@sas/nova-commons/hooks/usePreviousValue';
import { useParentSize } from '@sas/nova-commons/hooks/useParentSize';
import { useIconA11yInfo } from '@sas/nova-commons/components/Icon';
import { useDirectSpeech } from '@sas/nova-commons/components/DirectSpeech';
import { useFormatLocale } from '@sas/nova-commons/locale/formatting';
import cn from './MainSearchPanel.css.json';
import './MainSearchPanel.css';
/**
 * Returns {true} if the given list item is a {PanelListItem}
 */
const isPanelListItem = (object) => {
    return 'item' in object;
};
export const MainSearchPanelBase = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    // Hooks
    // =============================================================================
    const localize = useI18n();
    const dt = useDesignTokens();
    const [setListParentRef, listParentDimensions] = useParentSize({
        // Initial dimensions of 0 prevent the table from flashing at the wrong size
        initialDimensions: { height: 0, width: 0 },
    });
    const formatLocale = useFormatLocale();
    // Local states
    // ===============================================================================
    const [demographicsDialogState, setDemographicsDialogState] = useState({
        featureIndex: -1,
        resultsDialogIsOpen: false,
        selectorDialogIsOpen: false,
    });
    const prevSearchTerm = usePreviousValue(props.searchTerm);
    const selectedSearchItems = (_b = (_a = props.searchResults) === null || _a === void 0 ? void 0 : _a.filter((item) => item.selected)) !== null && _b !== void 0 ? _b : [];
    const selectedFeatureItems = (_d = (_c = props.features) === null || _c === void 0 ? void 0 : _c.filter((item) => item.selected)) !== null && _d !== void 0 ? _d : [];
    /**
     * Returns the index for the given item state
     */
    const getItemIndex = useMemo(() => {
        return (itemState) => {
            if (props.searchHints && isSearchHint(itemState)) {
                return props.searchHints.indexOf(itemState);
            }
            if (props.searchResults && isSearchResult(itemState)) {
                return props.searchResults.indexOf(itemState);
            }
            if (props.features && isGeographicFeature(itemState)) {
                return props.features.indexOf(itemState);
            }
            return -1;
        };
    }, [props.searchHints, props.searchResults, props.features]);
    /**
     * Updates the visible property for the given item states
     */
    const onVisibilityStateChange = (searchItems, featureItems, visible) => {
        props.actions.notifyVisibilityChange(props.id, {
            searchResults: searchItems.map(getItemIndex),
            features: featureItems.map(getItemIndex),
            value: visible,
        });
    };
    /**
     * Builds the UI model for the main item list
     */
    const items = useMemo(() => {
        var _a, _b, _c;
        const onNavigationButtonPress = (itemState) => {
            const index = getItemIndex(itemState);
            props.actions.notifyUpdateSelection(props.id, itemState.type === 'result'
                ? { searchResults: [index], navigate: true }
                : { features: [index], navigate: true });
        };
        const items = [];
        if ((_a = props.searchHints) === null || _a === void 0 ? void 0 : _a.length) {
            const group = localize({ key: 'MainSearchPanel.searchHints.txt' });
            items.push(...props.searchHints.map((item) => {
                return {
                    item,
                    group,
                    description: getItemDescription(item, localize, formatLocale),
                    onNavigationButtonPress,
                };
            }));
        }
        if ((_b = props.searchResults) === null || _b === void 0 ? void 0 : _b.length) {
            const group = localize({ key: 'MainSearchPanel.searchResults.txt' });
            items.push({
                title: group,
                className: cn.groupItemHeader,
            }, ...props.searchResults.map((item, index) => {
                return {
                    item,
                    group,
                    counter: index + 1,
                    description: getItemDescription(item, localize, formatLocale),
                    onVisibilityButtonPress: () => props.actions.notifyUpdateSearchResult(props.id, {
                        index,
                        update: { hidden: !item.hidden },
                    }),
                    onNavigationButtonPress,
                };
            }));
        }
        if ((_c = props.features) === null || _c === void 0 ? void 0 : _c.length) {
            const group = localize({ key: 'MainSearchPanel.features.txt' });
            items.push({
                title: group,
                className: cn.groupItemHeader,
            }, ...props.features.map((item, index) => {
                return Object.assign(Object.assign({ item,
                    group, description: getItemDescription(item, localize, formatLocale), onVisibilityButtonPress: (item) => {
                        if (isSearchHint(item)) {
                            return;
                        }
                        props.actions.notifyUpdatePin(props.id, {
                            index,
                            update: { hidden: !item.hidden },
                        });
                    } }, (item.type === 'area' && {
                    onShowDemographicButtonPress: () => {
                        setDemographicsDialogState({
                            featureIndex: index,
                            selectorDialogIsOpen: false,
                            resultsDialogIsOpen: true,
                        });
                    },
                })), { onNavigationButtonPress });
            }));
        }
        return items;
    }, [
        props.id,
        props.searchResults,
        props.searchHints,
        props.features,
        props.actions,
        localize,
        formatLocale,
        getItemIndex,
        setDemographicsDialogState,
    ]);
    const selectedItemIndices = useMemo(() => {
        const indices = []; // list of indices indicating the selection state of an item
        // find selected items
        items.forEach((itemProp, index) => {
            if ('item' in itemProp && 'selected' in itemProp.item && itemProp.item['selected'] === true) {
                indices.push(String(index));
            }
        });
        return indices;
    }, [items]);
    const firstSelectedItemIndex = useRef(selectedItemIndices.length > 0 ? parseInt(selectedItemIndices[0]) : -1);
    const listState = useListState();
    const { setSelectRows, scrollTo, clearMeasurementCache } = listState;
    useEffect(() => {
        setSelectRows({
            selectedRows: new Set(selectedItemIndices),
        });
        clearMeasurementCache({ startRowIndex: 0, startColumnIndex: 0 });
    }, [items, setSelectRows, selectedItemIndices, clearMeasurementCache]);
    // first render - scroll to the first selected item
    // TODO: move to useListState({scrollPosition:...}) once NOVACOMMONS-18123 is resolved
    useEffect(() => {
        if (firstSelectedItemIndex.current > 0) {
            scrollTo({
                cell: {
                    rowIndex: firstSelectedItemIndex.current,
                    colIndex: 0,
                    region: TableRegions.Data,
                },
                focusToCell: false,
                align: 'center',
            });
        }
    }, [scrollTo]);
    // scroll to the top of the list if search term changes
    useEffect(() => {
        if (prevSearchTerm !== undefined && prevSearchTerm !== props.searchTerm) {
            scrollTo({
                cell: {
                    rowIndex: 0,
                    colIndex: 0,
                    region: TableRegions.Data,
                },
                focusToCell: false,
                align: 'center',
            });
        }
    }, [scrollTo, prevSearchTerm, props.searchTerm]);
    const spacingTokens = renderInlineCssVars({
        'spacing-s': dt.tokens['space-base-s'],
        'spacing-m': dt.tokens['space-base-m'],
    }, 'mainSearch');
    const buttonTokens = renderInlineCssVars({ 'hover-bg': 'transparent' }, 'button');
    const narration = useDirectSpeech();
    const getIconA11yInfo = useIconA11yInfo();
    return (_jsxs("div", Object.assign({ className: cn.container, style: spacingTokens }, { children: [_jsxs("div", Object.assign({ className: cn.header }, { children: [_jsx(Button, { className: cn.button, style: buttonTokens, variant: ButtonVariant.Icon, icon: { icon: Location, tooltip: localize({ key: 'MainSearchPanel.close.label.txt' }) }, onPress: props.onCollapse }), _jsx(AutoFocusInside, Object.assign({ className: cn.searchFieldContainer }, { children: _jsx(SearchField, { className: cn.searchField, onChange: (e) => props.actions.update(props.id, { searchTerm: e.value }), onCommit: (e) => props.actions.notifySearch(props.id, { term: e.value }), value: (_e = props.searchTerm) !== null && _e !== void 0 ? _e : '', numberOfResults: props.searchTerm ? (_g = (_f = props.searchResults) === null || _f === void 0 ? void 0 : _f.length) !== null && _g !== void 0 ? _g : null : null }) }))] })), _jsxs("div", Object.assign({ className: cn.contentContainer }, { children: [_jsx("div", Object.assign({ className: cn.list }, { children: _jsx("div", Object.assign({ ref: setListParentRef }, { children: _jsx(List, Object.assign({ tableTheme: () => {
                                    return {
                                        '--table-scroll-bar-space': '1rem',
                                    };
                                } }, listState, { selectionMode: "desktop", rowCount: items.length, showSeparator: () => false, itemHeight: () => 'double', height: listParentDimensions === null || listParentDimensions === void 0 ? void 0 : listParentDimensions.height, width: listParentDimensions === null || listParentDimensions === void 0 ? void 0 : listParentDimensions.width, iconColumns: [
                                    {
                                        key: 'demographics',
                                        cellIcon: (rowIndex) => {
                                            var _a;
                                            const item = items[rowIndex];
                                            const hasDemographics = isPanelListItem(item) &&
                                                item.item.type === 'area' &&
                                                Object.keys(((_a = item.item.demographics) === null || _a === void 0 ? void 0 : _a.data) || {}).length > 0;
                                            return hasDemographics
                                                ? {
                                                    icon: UserSession,
                                                    tooltip: localize({ key: 'MainSearchPanel.demographics.tooltip.txt' }),
                                                }
                                                : null;
                                        },
                                        onPress: (rowIndex) => {
                                            () => {
                                                var _a;
                                                const item = items[rowIndex];
                                                if (!isPanelListItem(item)) {
                                                    return;
                                                }
                                                (_a = item.onShowDemographicButtonPress) === null || _a === void 0 ? void 0 : _a.call(item);
                                            };
                                        },
                                    },
                                    {
                                        key: 'visibility',
                                        cellIcon: (rowIndex) => {
                                            const item = items[rowIndex];
                                            if (!isPanelListItem(item) || isSearchHint(item.item)) {
                                                return null;
                                            }
                                            return {
                                                icon: item.item.hidden ? Hidden : Visible,
                                                style: {
                                                    '--icon-default-override-fg': item.item.hidden
                                                        ? dt.reportTokens.text.description.fg
                                                        : undefined,
                                                },
                                            };
                                        },
                                        onPress: (rowIndex) => {
                                            var _a;
                                            const item = items[rowIndex];
                                            if (!isPanelListItem(item) || isSearchHint(item.item)) {
                                                return;
                                            }
                                            (_a = item.onVisibilityButtonPress) === null || _a === void 0 ? void 0 : _a.call(item, item.item);
                                            const { ariaLabel, tooltip } = getIconA11yInfo({
                                                icon: item.item.hidden ? Visible : Hidden,
                                            });
                                            const notification = ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : tooltip;
                                            if (narration && notification) {
                                                narration.notify(notification);
                                            }
                                        },
                                    },
                                    {
                                        key: 'navigate',
                                        cellIcon: (rowIndex) => {
                                            const item = items[rowIndex];
                                            if (!isPanelListItem(item) || isSearchHint(item.item)) {
                                                return null;
                                            }
                                            return {
                                                icon: DisclosureNext,
                                                variant: ButtonVariant.RoundIcon,
                                                tooltip: localize({ key: 'MainSearchPanel.navigate.tooltip.txt' }),
                                            };
                                        },
                                        onPress: (rowIndex) => {
                                            var _a;
                                            const item = items[rowIndex];
                                            if (!isPanelListItem(item)) {
                                                return item;
                                            }
                                            (_a = item.onNavigationButtonPress) === null || _a === void 0 ? void 0 : _a.call(item, item.item);
                                        },
                                        // List isn't reserving space for the scrollbar in our Storybook
                                        width: 4 /* margin */ + 32 /* button */ + 16 /* scrollbar */,
                                        minimumWidth: 4 + 32 + 16,
                                        maximumWidth: 4 + 32 + 16,
                                    },
                                ], itemColumn: {
                                    key: 'test',
                                    minimumWidth: 0,
                                    item(index) {
                                        const item = items[index];
                                        return isPanelListItem(item) ? (_jsx(PanelListItem, Object.assign({}, item))) : (_jsx(GroupHeaderItem, Object.assign({}, item)));
                                    },
                                    colSpan({ rowIndex }) {
                                        var _a;
                                        const item = items[rowIndex];
                                        // Doesn't apply to headers
                                        if (!isPanelListItem(item)) {
                                            return 1;
                                        }
                                        // Hints have no icons and span all columns
                                        if (isSearchHint(item.item)) {
                                            return 4;
                                        }
                                        // The first icon is only used if the item has demographics
                                        if (item.item.type === 'area' &&
                                            Object.keys(((_a = item.item.demographics) === null || _a === void 0 ? void 0 : _a.data) || {}).length) {
                                            return 1;
                                        }
                                        // Span into demographic column by default
                                        return 2;
                                    },
                                }, 
                                // Needed when itemColumn doesn't return a GroupHeaderItem or ListItem
                                getItemInfo: (index) => {
                                    const item = items[index];
                                    if (!isPanelListItem(item)) {
                                        return;
                                    }
                                    switch (item.item.type) {
                                        case 'hint':
                                            return {
                                                type: Item,
                                                props: {
                                                    title: item.item.name,
                                                    description: item.description,
                                                    group: localize({ key: 'MainSearchPanel.searchHints.txt' }),
                                                },
                                            };
                                        case 'result':
                                            return {
                                                type: Item,
                                                props: {
                                                    title: item.item.name,
                                                    description: item.description,
                                                    group: localize({ key: 'MainSearchPanel.searchResults.txt' }),
                                                },
                                            };
                                        default:
                                            return {
                                                type: Item,
                                                props: {
                                                    title: item.item.name,
                                                    description: item.description,
                                                    group: localize({ key: 'MainSearchPanel.features.txt' }),
                                                },
                                            };
                                    }
                                }, onSelect: (selectedRows) => {
                                    if (!selectedRows) {
                                        return;
                                    }
                                    const searchResults = [];
                                    const features = [];
                                    for (const itemIndex of Array.from(selectedRows)) {
                                        const listItem = items[parseInt(itemIndex)];
                                        if (isPanelListItem(listItem)) {
                                            if (isSearchHint(listItem.item)) {
                                                props.actions.notifySearch(props.id, {
                                                    term: listItem.item.name,
                                                    autoCreate: !listItem.item.isMultiple,
                                                });
                                                return;
                                            }
                                            if (isSearchResult(listItem.item)) {
                                                const index = getItemIndex(listItem.item);
                                                searchResults.push(index);
                                            }
                                            if (isGeographicFeature(listItem.item)) {
                                                const index = getItemIndex(listItem.item);
                                                features.push(index);
                                                props.actions.update(props.id, { searchHints: [] });
                                            }
                                        }
                                    }
                                    props.actions.notifyUpdateSelection(props.id, {
                                        searchResults,
                                        features,
                                    });
                                } })) })) })), demographicsDialogState.featureIndex >= 0 ? (_jsx(Demographics, { id: props.id, dialogOpenState: demographicsDialogState, setDialogOpenState: setDemographicsDialogState })) : null, _jsx(Divider, { variant: "standard" }), _jsxs(Flow, Object.assign({ className: cn.footer, orientation: "horizontal" }, { children: [_jsxs(Flow, Object.assign({ orientation: "horizontal", role: "group", className: cn.footerToolbar, spacing: "xxs" }, { children: [_jsx(Button, { className: cn.footerToolbarButton, variant: ButtonVariant.Light, ariaLabel: localize({
                                            key: 'MainSearchPanel.pin.txt',
                                        }), icon: {
                                            icon: Location,
                                        }, text: localize({
                                            key: 'MainSearchPanel.pin.txt',
                                        }), tooltip: localize({
                                            key: 'MainSearchPanel.pin.tooltip.txt',
                                        }), onPress: () => props.actions.notifyNewPin(props.id) }), _jsx(Button, { className: cn.footerToolbarButton, variant: ButtonVariant.Light, ariaLabel: localize({
                                            key: 'MainSearchPanel.route.txt',
                                        }), icon: {
                                            icon: AddRoute,
                                        }, text: localize({
                                            key: 'MainSearchPanel.route.txt',
                                        }), tooltip: localize({
                                            key: 'MainSearchPanel.route.tooltip.txt',
                                        }), onPress: () => props.actions.notifyNewRoute(props.id) }), props.canDoDistanceSelections ? (_jsx(Button, { className: cn.footerToolbarButton, variant: ButtonVariant.Light, ariaLabel: localize({
                                            key: 'MainSearchPanel.geographicArea.txt',
                                        }), icon: {
                                            icon: GeoUIAreaAddress,
                                            tooltip: localize({
                                                key: 'MainSearchPanel.geographicArea.txt',
                                            }),
                                        }, text: localize({
                                            key: 'MainSearchPanel.geographicArea.txt',
                                        }), tooltip: localize({
                                            key: 'MainSearchPanel.geographicArea.tooltip.txt',
                                        }), onPress: () => props.actions.notifyNewArea(props.id) })) : null] })), _jsx(MenuButton, { className: cn.overflowButton, disabled: !(((_h = props.features) === null || _h === void 0 ? void 0 : _h.length) || ((_j = props.searchResults) === null || _j === void 0 ? void 0 : _j.length)), onItemSelected: (location) => {
                                    var _a, _b, _c;
                                    let searchItems = selectedSearchItems;
                                    let featureItems = selectedFeatureItems;
                                    if (searchItems.length + featureItems.length === 0) {
                                        // no selection so use all
                                        searchItems = (_a = props.searchResults) !== null && _a !== void 0 ? _a : [];
                                        featureItems = (_b = props.features) !== null && _b !== void 0 ? _b : [];
                                    }
                                    const section = location[0];
                                    if (section.length === 2) {
                                        if (section[0] === 0 && section[1] === 0) {
                                            onVisibilityStateChange(searchItems, featureItems, true);
                                        }
                                        else if (section[0] === 0 && section[1] === 1) {
                                            onVisibilityStateChange(searchItems, featureItems, false);
                                        }
                                        else if (section[0] === 0 && section[1] === 2) {
                                            if (!selectedSearchItems.length && !selectedFeatureItems.length) {
                                                if ((_c = props.searchResults) === null || _c === void 0 ? void 0 : _c.length) {
                                                    props.actions.notifyDeleteSearchResults(props.id, props.searchResults.map(getItemIndex));
                                                }
                                                props.actions.notifyDeleteAllPins(props.id);
                                            }
                                            if (selectedSearchItems.length) {
                                                props.actions.notifyDeleteSearchResults(props.id, selectedSearchItems.map(getItemIndex));
                                            }
                                            if (selectedFeatureItems.length) {
                                                props.actions.notifyDeletePins(props.id, selectedFeatureItems.map(getItemIndex));
                                            }
                                            // } else if (section[0] === 1 && section[1] === 0) {
                                            //   // show pin labels
                                            //   // TODO: implement
                                        }
                                        else if (section[0] === 1 && section[1] === 0) {
                                            if (selectedSearchItems.length > 0) {
                                                // convert selected search results
                                                props.actions.notifyConvertSearchResults(props.id, selectedSearchItems.map(getItemIndex));
                                            }
                                            else {
                                                // convert all search result to pin
                                                props.actions.notifyConvertAllSearchResults(props.id);
                                            }
                                        }
                                    }
                                }, sections: [
                                    {
                                        items: [
                                            {
                                                text: localize({
                                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                                        ? 'MainSearchPanel.show.txt'
                                                        : 'MainSearchPanel.show.all.txt',
                                                }),
                                            },
                                            {
                                                text: localize({
                                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                                        ? 'MainSearchPanel.hide.txt'
                                                        : 'MainSearchPanel.hide.all.txt',
                                                }),
                                            },
                                            {
                                                text: localize({
                                                    key: selectedSearchItems.length > 0 || selectedFeatureItems.length > 0
                                                        ? 'MainSearchPanel.delete.txt'
                                                        : 'MainSearchPanel.delete.all.txt',
                                                }),
                                            },
                                        ],
                                    },
                                    // {
                                    //   items: [
                                    //       {
                                    //         text: localize({ key: 'MainSearchPanel.showPinLabels.txt' }),
                                    //         disabled: props.features?.filter((item) => item?.type === 'pin').length === 0
                                    //       }
                                    //   ],
                                    // },
                                    {
                                        items: [
                                            {
                                                disabled: !props.searchResults || props.searchResults.length === 0,
                                                text: localize({
                                                    key: selectedSearchItems.length > 0
                                                        ? 'MainSearchPanel.convert.txt'
                                                        : 'MainSearchPanel.convert.all.txt',
                                                }),
                                            },
                                        ],
                                    },
                                ], variant: ButtonVariant.Light, icon: { icon: Overflow } })] }))] }))] })));
};
export const MainSearchPanel = connect((state, props) => {
    var _a;
    const { features, searchTerm, searchResults, searchHints, canDoPremiumServices, canDoDistanceSelections, } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    return {
        searchTerm,
        searchHints,
        searchResults,
        features,
        canDoPremiumServices,
        canDoDistanceSelections,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(MainSearchPanelBase);
